import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'

/**
 * Top 10 / Bottom 10 leases by prior-day oil production vs forecast.
 *
 * Data comes from /api/well-analysis/underperformers?period=1d, which returns
 * every ComboCurveTie (lease) that has a forecast for the most recent complete
 * production day (2-day lag), sorted ascending by delta. Bottom 10 = first 10,
 * Top 10 = last 10 reversed.
 *
 * Clicking a lease drills through to Well Analysis pre-filtered on that lease.
 */

// --- Icons -----------------------------------------------------------------

// Plane taking off — beating forecast
const IconTakeoff = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z" />
    </svg>
)

// Plane landing — trailing forecast
const IconLanding = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M2.5 19h19v2h-19v-2zm7.18-5.73l4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.43z" />
    </svg>
)

const IconArrowUp = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
)

const IconArrowDown = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
)

const IconChevron = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
)

// --- Helpers ---------------------------------------------------------------

const fmt1 = (n) => (n == null || !isFinite(n) ? '-' : Number(n).toFixed(1))
const fmtSigned = (n) => (n == null || !isFinite(n) ? '-' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}`)

const formatDataDate = (d) => {
    if (!d) return null
    // "2026-08-22" - parse as local, not UTC, so the day doesn't shift back
    const [y, m, day] = String(d).slice(0, 10).split('-').map(Number)
    if (!y || !m || !day) return d
    return new Date(y, m - 1, day).toLocaleDateString(undefined, {
        weekday: 'short', month: 'numeric', day: 'numeric'
    })
}

// --- Single lease row ------------------------------------------------------

function LeaseRow({ row, rank, isDark, onClick }) {
    const beat = (row.delta ?? 0) >= 0
    const actual = row.oilProduction ?? 0
    const forecast = row.oilForecast ?? 0

    // Bar shows actual against the larger of the two, so over- and
    // under-performance are both readable on the same scale.
    const scale = Math.max(actual, forecast) || 1
    const actualPct = Math.min(100, (actual / scale) * 100)
    const forecastPct = Math.min(100, (forecast / scale) * 100)

    return (
        <button
            onClick={onClick}
            title={`${row.comboName} - open in Well Analysis`}
            className={`group w-full text-left px-3 py-2 transition-colors ${
                isDark ? 'hover:bg-slate-800/70' : 'hover:bg-gray-50'
            }`}
        >
            <div className="flex items-center gap-2">
                <span className={`w-4 flex-shrink-0 text-[10px] font-mono tabular-nums ${
                    isDark ? 'text-gray-600' : 'text-gray-400'
                }`}>
                    {rank}
                </span>

                <span className={`flex-1 min-w-0 truncate text-xs font-medium ${
                    isDark
                        ? 'text-gray-200 group-hover:text-blue-300'
                        : 'text-gray-800 group-hover:text-blue-700'
                }`}>
                    {row.comboName}
                </span>

                <span className={`flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${
                    beat
                        ? isDark ? 'bg-green-900/60 text-green-300' : 'bg-green-100 text-green-800'
                        : isDark ? 'bg-red-900/60 text-red-300' : 'bg-red-100 text-red-800'
                }`}>
                    {beat ? <IconArrowUp className="w-2.5 h-2.5" /> : <IconArrowDown className="w-2.5 h-2.5" />}
                    {fmtSigned(row.variancePct)}%
                </span>

                <IconChevron className={`w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
            </div>

            <div className="flex items-center gap-2 mt-1 pl-6">
                <div className={`relative flex-1 h-1.5 rounded-full overflow-hidden ${
                    isDark ? 'bg-slate-800' : 'bg-gray-200'
                }`}>
                    <div
                        className={`absolute inset-y-0 left-0 rounded-full ${beat ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${actualPct}%` }}
                    />
                    {/* forecast tick */}
                    <div
                        className={`absolute inset-y-0 w-0.5 ${isDark ? 'bg-gray-300' : 'bg-gray-700'}`}
                        style={{ left: `calc(${forecastPct}% - 1px)` }}
                    />
                </div>
                <span className={`flex-shrink-0 text-[10px] tabular-nums ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {fmt1(actual)}<span className="opacity-60"> / {fmt1(forecast)}</span>
                </span>
                <span className={`flex-shrink-0 w-12 text-right text-[10px] font-semibold tabular-nums ${
                    beat
                        ? isDark ? 'text-green-400' : 'text-green-700'
                        : isDark ? 'text-red-400' : 'text-red-700'
                }`}>
                    {fmtSigned(row.delta)}
                </span>
            </div>
        </button>
    )
}

// --- One ranked panel (Top or Bottom) --------------------------------------

function RankPanel({ variant, rows, dataDate, loading, isDark, onSelect }) {
    const isTop = variant === 'top'
    const Icon = isTop ? IconTakeoff : IconLanding

    const totalDelta = rows.reduce((s, r) => s + (r.delta || 0), 0)
    const totalActual = rows.reduce((s, r) => s + (r.oilProduction || 0), 0)
    const totalForecast = rows.reduce((s, r) => s + (r.oilForecast || 0), 0)

    return (
        <div className={`rounded-lg border shadow-lg overflow-hidden ${
            isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className={`px-3 py-2.5 border-b ${
                isDark
                    ? isTop ? 'border-slate-800 bg-green-950/30' : 'border-slate-800 bg-red-950/30'
                    : isTop ? 'border-gray-200 bg-green-50' : 'border-gray-200 bg-red-50'
            }`}>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded ${
                            isTop
                                ? isDark ? 'bg-green-900/70 text-green-300' : 'bg-green-100 text-green-700'
                                : isDark ? 'bg-red-900/70 text-red-300' : 'bg-red-100 text-red-700'
                        }`}>
                            <Icon className="w-4 h-4" />
                        </span>
                        <div className="min-w-0">
                            <h3 className={`text-xs font-bold uppercase tracking-wide ${
                                isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                                {isTop ? 'Top 10' : 'Btm 10'}
                            </h3>
                            <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                Oil vs Forecast{dataDate ? ` · ${formatDataDate(dataDate)}` : ''}
                            </p>
                        </div>
                    </div>

                    {/* Panel KPI */}
                    {!loading && rows.length > 0 && (
                        <div className="flex-shrink-0 text-right">
                            <div className={`text-sm font-bold tabular-nums ${
                                totalDelta >= 0
                                    ? isDark ? 'text-green-400' : 'text-green-700'
                                    : isDark ? 'text-red-400' : 'text-red-700'
                            }`}>
                                {fmtSigned(totalDelta)}
                                <span className="text-[10px] font-normal opacity-70"> BBL</span>
                            </div>
                            <div className={`text-[10px] tabular-nums ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                {Math.round(totalActual)} / {Math.round(totalForecast)}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Rows */}
            {loading ? (
                <div className="p-3 space-y-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className={`h-6 rounded animate-pulse ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <p className={`px-3 py-6 text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    No forecast data for this day
                </p>
            ) : (
                <div className={`divide-y ${isDark ? 'divide-slate-800/70' : 'divide-gray-100'}`}>
                    {rows.map((row, i) => (
                        <LeaseRow
                            key={row.comboName}
                            row={row}
                            rank={i + 1}
                            isDark={isDark}
                            onClick={() => onSelect(row.comboName)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

// --- Public component ------------------------------------------------------

function LeaseForecastRanking({ data, dataDate, loading }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const navigate = useNavigate()

    // API returns ascending by delta across all leases that have a forecast
    const { top, bottom, leaseCount } = useMemo(() => {
        const rows = (data || []).filter(r => r && r.comboName)
        const asc = [...rows].sort((a, b) => (a.delta || 0) - (b.delta || 0))
        return {
            bottom: asc.slice(0, 10),
            top: asc.slice(-10).reverse(),
            leaseCount: asc.length
        }
    }, [data])

    const handleSelect = (comboName) => {
        navigate(`/well-analysis?combo=${encodeURIComponent(comboName)}`)
    }

    return (
        <div className="space-y-4">
            <RankPanel
                variant="top" rows={top} dataDate={dataDate}
                loading={loading} isDark={isDark} onSelect={handleSelect}
            />
            <RankPanel
                variant="bottom" rows={bottom} dataDate={dataDate}
                loading={loading} isDark={isDark} onSelect={handleSelect}
            />
            {!loading && leaseCount > 0 && (
                <button
                    onClick={() => navigate('/well-analysis')}
                    className={`w-full text-center text-[11px] py-2 rounded-lg border transition ${
                        isDark
                            ? 'border-slate-800 text-blue-400 hover:bg-slate-900 hover:border-blue-800'
                            : 'border-gray-200 text-blue-600 hover:bg-gray-50 hover:border-blue-300'
                    }`}
                >
                    View all {leaseCount} leases in Well Analysis &rarr;
                </button>
            )}
        </div>
    )
}

export default LeaseForecastRanking
